import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Phone,
  Plus,
  Upload,
  Download,
  CalendarIcon,
  Edit,
  Trash2,
  MoreVertical,
  AlertTriangle,
  Clock,
  CheckCircle,
  FileText,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function TelephoneDiscussions() {
  const { user } = useContext(AuthContext);
  
  // Check if user is view_only (read-only mode)
  const isViewOnly = user?.role === "view_only";
  
  const [sites, setSites] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [csvData, setCsvData] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");

  const [formData, setFormData] = useState({
    site_id: "",
    call_date: format(new Date(), "yyyy-MM-dd"),
    call_time: "",
    contact_name: "",
    contact_company: "",
    phone_number: "",
    discussion_topic: "",
    discussion_details: "",
    priority: "medium",
    follow_up_required: false,
    follow_up_date: "",
    status: "open"
  });

  const priorityColors = {
    low: "bg-gray-100 text-gray-700",
    medium: "bg-blue-100 text-blue-700",
    high: "bg-orange-100 text-orange-700",
    urgent: "bg-red-100 text-red-700"
  };

  const statusColors = {
    open: "bg-green-100 text-green-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    closed: "bg-gray-100 text-gray-700"
  };

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    if (selectedSite) {
      loadDiscussions();
    }
  }, [selectedSite, filterStatus, filterPriority]);

  const loadSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
        if (data.length > 0) {
          setSelectedSite(data[0].id);
          setFormData(prev => ({ ...prev, site_id: data[0].id }));
        }
      }
    } catch (error) {
      console.error("Error loading sites:", error);
      toast.error("Failed to load sites");
    } finally {
      setLoading(false);
    }
  };

  const loadDiscussions = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      
      if (selectedSite) params.append('site_id', selectedSite);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority && filterPriority !== 'all') params.append('priority', filterPriority);
      
      const response = await fetch(`${API}/telephone-discussions?${params.toString()}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDiscussions(data);
      }
    } catch (error) {
      console.error("Error loading discussions:", error);
      toast.error("Failed to load telephone discussions");
    }
  };

  const handleCreateDiscussion = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_name.trim() || !formData.discussion_topic.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/telephone-discussions`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Telephone discussion recorded successfully!");
        setShowCreateDialog(false);
        resetForm();
        loadDiscussions();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to create discussion record");
      }
    } catch (error) {
      console.error("Error creating discussion:", error);
      toast.error("Failed to create discussion record");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditDiscussion = (discussion) => {
    setSelectedDiscussion(discussion);
    setFormData({
      site_id: discussion.site_id,
      call_date: discussion.call_date,
      call_time: discussion.call_time || "",
      contact_name: discussion.contact_name,
      contact_company: discussion.contact_company,
      phone_number: discussion.phone_number,
      discussion_topic: discussion.discussion_topic,
      discussion_details: discussion.discussion_details,
      priority: discussion.priority,
      follow_up_required: discussion.follow_up_required,
      follow_up_date: discussion.follow_up_date || "",
      status: discussion.status
    });
    setShowEditDialog(true);
  };

  const handleUpdateDiscussion = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/telephone-discussions/${selectedDiscussion.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Discussion updated successfully!");
        setShowEditDialog(false);
        setSelectedDiscussion(null);
        resetForm();
        loadDiscussions();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to update discussion");
      }
    } catch (error) {
      console.error("Error updating discussion:", error);
      toast.error("Failed to update discussion");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDiscussion = (discussion) => {
    setSelectedDiscussion(discussion);
    setShowDeleteDialog(true);
  };

  const confirmDeleteDiscussion = async () => {
    if (!selectedDiscussion) return;

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/telephone-discussions/${selectedDiscussion.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Discussion deleted successfully!");
        setShowDeleteDialog(false);
        setSelectedDiscussion(null);
        loadDiscussions();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to delete discussion");
      }
    } catch (error) {
      console.error("Error deleting discussion:", error);
      toast.error("Failed to delete discussion");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImportCSV = async () => {
    if (!csvData.trim()) {
      toast.error("Please paste CSV data");
      return;
    }

    if (!selectedSite) {
      toast.error("Please select a site first");
      return;
    }

    setIsSubmitting(true);

    try {
      // Parse CSV data
      const lines = csvData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row = {};
        headers.forEach((header, index) => {
          row[header.toLowerCase().replace(/\s+/g, '_')] = values[index] || "";
        });
        return row;
      });

      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/telephone-discussions/import?site_id=${selectedSite}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(rows)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Imported ${result.imported_count} of ${result.total_rows} records`);
        
        if (result.errors.length > 0) {
          console.warn("Import errors:", result.errors);
          toast.warning(`${result.errors.length} rows had errors - check console for details`);
        }
        
        setShowImportDialog(false);
        setCsvData("");
        loadDiscussions();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to import data");
      }
    } catch (error) {
      console.error("Error importing CSV:", error);
      toast.error("Failed to import CSV data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportCSV = () => {
    if (discussions.length === 0) {
      toast.error("No data to export");
      return;
    }

    const csvHeaders = [
      "Call Date", "Call Time", "Contact Name", "Contact Company", 
      "Phone Number", "Discussion Topic", "Discussion Details", 
      "Priority", "Follow Up Required", "Follow Up Date", "Status"
    ];

    const csvData = discussions.map(discussion => [
      discussion.call_date,
      discussion.call_time || "",
      discussion.contact_name,
      discussion.contact_company,
      discussion.phone_number,
      discussion.discussion_topic,
      discussion.discussion_details,
      discussion.priority,
      discussion.follow_up_required ? "Yes" : "No",
      discussion.follow_up_date || "",
      discussion.status
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `telephone-discussions-${selectedSite}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    toast.success("Telephone discussions exported successfully!");
  };

  const resetForm = () => {
    setFormData({
      site_id: selectedSite,
      call_date: format(new Date(), "yyyy-MM-dd"),
      call_time: "",
      contact_name: "",
      contact_company: "",
      phone_number: "",
      discussion_topic: "",
      discussion_details: "",
      priority: "medium",
      follow_up_required: false,
      follow_up_date: "",
      status: "open"
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="discussions-loading">
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 gap-6">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-in-up" data-testid="telephone-discussions-container">
      {/* Header */}
      <div className="mb-8">
        <h1 className="heading-primary text-4xl mb-2">Telephone Discussion Records</h1>
        <p className="text-lg text-slate-600">Import and manage telephone conversation records</p>
      </div>

      {/* Filters and Actions */}
      <Card className="dashboard-card mb-8">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div className="space-y-2">
              <Label className="font-medium text-sm">Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="discussion-site-select">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-sm">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-sm">Priority</Label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger>
                  <SelectValue placeholder="All priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex space-x-2">
              {!isViewOnly && (
                <Button onClick={() => setShowCreateDialog(true)} className="btn-construction" data-testid="add-discussion-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Record
                </Button>
              )}
              
              <Button onClick={() => setShowImportDialog(true)} variant="outline" data-testid="import-discussions-button">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
            </div>

            <div>
              <Button onClick={exportCSV} variant="outline" disabled={discussions.length === 0} data-testid="export-discussions-button">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Discussions List */}
      {discussions.length === 0 ? (
        <Card className="dashboard-card" data-testid="no-discussions-card">
          <CardContent className="p-12 text-center">
            <Phone className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Telephone Discussions Found</h3>
            <p className="text-slate-600 mb-6">{isViewOnly ? "No telephone discussion records available" : "Start by adding or importing telephone discussion records"}</p>
            {!isViewOnly && (
              <div className="flex justify-center space-x-3">
                <Button onClick={() => setShowCreateDialog(true)} className="btn-construction">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Record
                </Button>
                <Button onClick={() => setShowImportDialog(true)} variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Import from CSV
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {discussions.map((discussion) => (
            <Card key={discussion.id} className="dashboard-card hover-lift" data-testid={`discussion-card-${discussion.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg text-slate-900">{discussion.discussion_topic}</h3>
                        <p className="text-sm text-slate-600 mt-1">
                          <strong>{discussion.contact_name}</strong> from <strong>{discussion.contact_company}</strong>
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={`text-xs ${priorityColors[discussion.priority]}`}>
                          {discussion.priority.toUpperCase()}
                        </Badge>
                        <Badge className={`text-xs ${statusColors[discussion.status]}`}>
                          {discussion.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <DropdownMenu>
                          {!isViewOnly && (
                            <>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditDiscussion(discussion)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Discussion
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteDiscussion(discussion)} 
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Discussion
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </>
                          )}
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-slate-700">Call Details</p>
                        <p className="text-slate-600">
                          {new Date(discussion.call_date).toLocaleDateString("en-AU")} 
                          {discussion.call_time && ` at ${discussion.call_time}`}
                        </p>
                        <p className="text-slate-600">{discussion.phone_number}</p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">Follow Up</p>
                        {discussion.follow_up_required ? (
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3 text-orange-500" />
                            <span className="text-slate-600">
                              {discussion.follow_up_date ? 
                                `Due ${new Date(discussion.follow_up_date).toLocaleDateString("en-AU")}` :
                                "Required"
                              }
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-1">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span className="text-slate-600">Not required</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">Created</p>
                        <p className="text-slate-600">
                          {new Date(discussion.created_at).toLocaleDateString("en-AU")}
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="font-medium text-slate-700 mb-1">Discussion Details</p>
                      <p className="text-slate-600 text-sm">{discussion.discussion_details}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Discussion Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[600px]" data-testid="create-discussion-dialog">
          <DialogHeader>
            <DialogTitle className="heading-secondary">Add Telephone Discussion Record</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDiscussion} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="call-date" className="font-medium">Call Date *</Label>
                <Input
                  id="call-date"
                  type="date"
                  value={formData.call_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, call_date: e.target.value }))}
                  required
                  data-testid="call-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="call-time" className="font-medium">Call Time</Label>
                <Input
                  id="call-time"
                  type="time"
                  value={formData.call_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, call_time: e.target.value }))}
                  data-testid="call-time-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-name" className="font-medium">Contact Name *</Label>
                <Input
                  id="contact-name"
                  placeholder="Full name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-company" className="font-medium">Contact Company *</Label>
                <Input
                  id="contact-company"
                  placeholder="Company name"
                  value={formData.contact_company}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_company: e.target.value }))}
                  required
                  data-testid="contact-company-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone-number" className="font-medium">Phone Number</Label>
              <Input
                id="phone-number"
                placeholder="+1 (555) 123-4567"
                value={formData.phone_number}
                onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                data-testid="phone-number-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="discussion-topic" className="font-medium">Discussion Topic *</Label>
              <Input
                id="discussion-topic"
                placeholder="Brief topic or subject of the call"
                value={formData.discussion_topic}
                onChange={(e) => setFormData(prev => ({ ...prev, discussion_topic: e.target.value }))}
                required
                data-testid="discussion-topic-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="discussion-details" className="font-medium">Discussion Details *</Label>
              <Textarea
                id="discussion-details"
                placeholder="Detailed notes about the conversation..."
                value={formData.discussion_details}
                onChange={(e) => setFormData(prev => ({ ...prev, discussion_details: e.target.value }))}
                rows={4}
                required
                data-testid="discussion-details-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="font-medium">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}>
                  <SelectTrigger data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Follow Up Date</Label>
                <Input
                  type="date"
                  value={formData.follow_up_date}
                  onChange={(e) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      follow_up_date: e.target.value,
                      follow_up_required: !!e.target.value 
                    }));
                  }}
                  data-testid="follow-up-date-input"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-construction" disabled={isSubmitting} data-testid="save-discussion-button">
                {isSubmitting ? "Saving..." : "Save Discussion"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[600px]" data-testid="edit-discussion-dialog">
          <DialogHeader>
            <DialogTitle className="heading-secondary">Edit Telephone Discussion</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateDiscussion} className="space-y-6">
            {/* Same form fields as create dialog */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-call-date" className="font-medium">Call Date *</Label>
                <Input
                  id="edit-call-date"
                  type="date"
                  value={formData.call_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, call_date: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-call-time" className="font-medium">Call Time</Label>
                <Input
                  id="edit-call-time"
                  type="time"
                  value={formData.call_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, call_time: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-contact-name" className="font-medium">Contact Name *</Label>
                <Input
                  id="edit-contact-name"
                  placeholder="Full name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-contact-company" className="font-medium">Contact Company *</Label>
                <Input
                  id="edit-contact-company"
                  placeholder="Company name"
                  value={formData.contact_company}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_company: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone-number" className="font-medium">Phone Number</Label>
              <Input
                id="edit-phone-number"
                placeholder="+1 (555) 123-4567"
                value={formData.phone_number}
                onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-discussion-topic" className="font-medium">Discussion Topic *</Label>
              <Input
                id="edit-discussion-topic"
                placeholder="Brief topic or subject of the call"
                value={formData.discussion_topic}
                onChange={(e) => setFormData(prev => ({ ...prev, discussion_topic: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-discussion-details" className="font-medium">Discussion Details *</Label>
              <Textarea
                id="edit-discussion-details"
                placeholder="Detailed notes about the conversation..."
                value={formData.discussion_details}
                onChange={(e) => setFormData(prev => ({ ...prev, discussion_details: e.target.value }))}
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="font-medium">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Follow Up Date</Label>
                <Input
                  type="date"
                  value={formData.follow_up_date}
                  onChange={(e) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      follow_up_date: e.target.value,
                      follow_up_required: !!e.target.value 
                    }));
                  }}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-construction" disabled={isSubmitting} data-testid="update-discussion-button">
                {isSubmitting ? "Updating..." : "Update Discussion"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-[700px]" data-testid="import-csv-dialog">
          <DialogHeader>
            <DialogTitle className="heading-secondary">Import Telephone Discussions from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 mb-2"><strong>CSV Format Requirements:</strong></p>
              <p className="text-xs text-blue-700 mb-2">Headers (first row): call_date, call_time, contact_name, contact_company, phone_number, discussion_topic, discussion_details, priority, follow_up_required, follow_up_date, status</p>
              <p className="text-xs text-blue-700"><strong>Example:</strong></p>
              <code className="text-xs text-blue-600 block mt-1 font-mono">
                2024-01-15,09:30,John Smith,ABC Corp,555-1234,Budget Discussion,Discussed project budget and timeline changes,high,true,2024-01-20,open
              </code>
            </div>

            <div className="space-y-2">
              <Label htmlFor="csv-data" className="font-medium">CSV Data</Label>
              <Textarea
                id="csv-data"
                placeholder="Paste your CSV data here..."
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                rows={10}
                className="font-mono text-sm"
                data-testid="csv-data-input"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowImportDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleImportCSV} className="btn-construction" disabled={isSubmitting} data-testid="import-csv-button">
                {isSubmitting ? "Importing..." : "Import CSV"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-discussion-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Telephone Discussion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this telephone discussion record?
              <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-800">
                  <strong>Topic:</strong> {selectedDiscussion?.discussion_topic}
                </p>
                <p className="text-sm text-red-800">
                  <strong>Contact:</strong> {selectedDiscussion?.contact_name} ({selectedDiscussion?.contact_company})
                </p>
              </div>
              <p className="mt-2 text-sm text-slate-600">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-discussion">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteDiscussion}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isSubmitting}
              data-testid="confirm-delete-discussion"
            >
              {isSubmitting ? "Deleting..." : "Delete Discussion"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}